import type { JSX } from 'react';
import { Outlet, useLocation } from 'react-router';

import { AppHeader } from '@/widgets/app-header';
import { ReplyNotice } from '@/widgets/reply-notice';
import { TabBar } from '@/widgets/tab-bar';

/**
 * The shell every patient screen lives in: one header, one tab bar, and a
 * scrolling column between them.
 *
 * Chrome is mounted once here rather than per page so it never re-mounts on a
 * tab change — a bar that blinks out and back is the clearest possible signal
 * that the app just reloaded itself, which it did not.
 *
 * The `key` on <main> is the pathname: it restarts the enter animation on every
 * navigation, so arriving on a tab reads as arriving somewhere rather than as
 * the content silently swapping underneath a static frame.
 */
export function ClientLayout(): JSX.Element {
  const location = useLocation();

  /*
    Booking is the one screen with a second column to place, so it is the one
    screen that asks for the wider measure. Everywhere else a single column of
    cards stretched across a desktop only makes each row harder to read — a
    measure is a reading decision, not free space to fill.
  */
  const wide = location.pathname === '/';

  return (
    <div className="min-h-dvh">
      {/* the page's light source: a single soft wash at the top, nothing louder */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 h-72 bg-gradient-to-b from-[var(--material-wash)] to-transparent"
      />

      <AppHeader wide={wide} />

      <main
        className={[
          'page-enter relative mx-auto flex w-full flex-col gap-6 px-4 pt-5 pb-tabbar',
          wide ? 'max-w-3xl lg:max-w-5xl' : 'max-w-3xl',
        ].join(' ')}
        key={location.pathname}
      >
        <Outlet />
      </main>

      {/* «پاسخ تازه از پزشک» — floats just above the tab bar */}
      <ReplyNotice />

      <TabBar />
    </div>
  );
}
